import { api } from "../api.js";
import { shell, pageHeading, statusPanel, hydrateCommitteeIdentity, championshipHeading } from "../components.js";
import {
  committeeFromStandings,
  escapeHtml,
  formatMatchDate,
  errorMessage,
  nonRedundantSubtitle,
  points,
  safeUrl,
  selectedCommittee,
  setSelectedCommittee,
} from "../utils.js";

const rankColorCodes = {
  1: "promossa",
  2: "playoff",
  3: "playoff",
  4: "playout",
  5: "playout",
  6: "retrocessa",
  7: "retrocessa",
  8: "promossa",
};

function formatQuotient(value, maximum, games) {
  if (Number(games) === 0) return "–";
  if (Number(value) === maximum) return "Max";
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? numeric.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : value;
}

function teamResultsMarkup(matches, teamId, teamName) {
  const played = matches.filter((match) => match.played && (
    String(match.team1?.id) === String(teamId) || String(match.team2?.id) === String(teamId)
  ));
  const content = played.length ? played.map((match) => {
    const homeSelected = String(match.team1?.id) === String(teamId);
    const setScores = points(match);
    return `<div class="team-result">
      <div class="team-result-meta"><strong>Gara ${escapeHtml(match.ng || "")}</strong><span>${escapeHtml(formatMatchDate(match.date))} · ${escapeHtml(match.time || "")}</span></div>
      <div class="team-result-fixture">
        <span class="${homeSelected ? "selected-team" : ""}">${escapeHtml(match.team1?.title || "")}</span>
        <strong class="team-result-score">${escapeHtml(match["team1-setwin"] ?? "")}–${escapeHtml(match["team2-setwin"] ?? "")}</strong>
        <span class="${homeSelected ? "" : "selected-team"}">${escapeHtml(match.team2?.title || "")}</span>
      </div>
      <div class="team-result-sets"><span class="sr-only">Parziali: </span>${setScores || "–"}</div>
    </div>`;
  }).join("") : '<p class="team-results-empty">Nessuna gara giocata.</p>';
  return `<tr class="standings-detail-row" id="team-results-${escapeHtml(teamId)}"><td colspan="12">
    <div class="team-results"><div class="team-results-heading"><strong>${escapeHtml(teamName)}</strong><span>${played.length} ${played.length === 1 ? "gara giocata" : "gare giocate"}</span></div>${content}</div>
  </td></tr>`;
}

export async function standingsView(params, signal) {
  let committee = selectedCommittee();
  const { group } = params;
  document.title = "Classifica | FIPAV Online";
  const renderShell = () => {
    document.querySelector("#app").innerHTML = shell(
      pageHeading("Classifica", "", `/gironi/${group}`) +
      `<div id="standings-content">${statusPanel("Caricamento classifica…")}</div>`, { committee },
    );
  };
  renderShell();
  try {
    const payload = await api.standings(group, signal);
    const inferredCommittee = committeeFromStandings(payload);
    if (inferredCommittee && inferredCommittee !== committee) {
      committee = inferredCommittee;
      setSelectedCommittee(committee);
      renderShell();
    }
    hydrateCommitteeIdentity(api, committee, signal);
    const standings = Array.isArray(payload) ? payload[0]?.data : payload?.data;
    const teams = standings?.teams || [];
    const subtitle = nonRedundantSubtitle(standings?.title || standings?.descrizione, standings?.["sub-title"]);
    const presentStatuses = new Set(teams.map((team) => rankColorCodes[Number(team.colorCode)]).filter(Boolean));
    const rows = teams.map((team, index) => {
      const teamName = team.title || team.squadra || team.nome_squadra || "";
      return `<tr class="standings-team-row ${rankColorCodes[Number(team.colorCode)] || ""}" data-team-id="${escapeHtml(team.id)}" data-team-name="${escapeHtml(teamName)}" tabindex="0" aria-expanded="false" aria-controls="team-results-${escapeHtml(team.id)}" aria-label="Mostra le gare giocate da ${escapeHtml(teamName)}">
      <td class="rank">${escapeHtml(team.pos ?? index + 1)}</td>
      <td class="team-name"><img src="${safeUrl(team.logo)}" alt="" loading="lazy"><strong>${escapeHtml(teamName)}</strong><span class="standing-row-indicator" aria-hidden="true">⌄</span></td>
      <td class="points">${escapeHtml(team.p ?? "0")}</td>
      <td>${escapeHtml(team.g ?? "0")}</td>
      <td>${escapeHtml(team.gv ?? "0")}</td>
      <td>${escapeHtml(team.gp ?? "0")}</td>
      <td>${escapeHtml(team.sv ?? "0")}</td>
      <td>${escapeHtml(team.sp ?? "0")}</td>
      <td>${escapeHtml(formatQuotient(team.qs ?? "", 100, team.g))}</td>
      <td>${escapeHtml(team.pf ?? "0")}</td>
      <td>${escapeHtml(team.ps ?? "0")}</td>
      <td>${escapeHtml(formatQuotient(team.qp ?? "", 1000, team.g))}</td>
    </tr>`;
    }).join("");
    const legend = presentStatuses.size ? `<div class="standings-legend" aria-label="Legenda classifica">
      <strong>Legenda</strong>
      ${presentStatuses.has("promossa") ? '<span class="promossa">Promossa</span>' : ""}
      ${presentStatuses.has("playoff") ? '<span class="playoff">Playoff</span>' : ""}
      ${presentStatuses.has("playout") ? '<span class="playout">Playout</span>' : ""}
      ${presentStatuses.has("retrocessa") ? '<span class="retrocessa">Retrocessa</span>' : ""}
    </div>` : "";
    document.querySelector("#standings-content").innerHTML = teams.length ? `<section class="container standings-wrap championship-layout">
      ${championshipHeading(group, standings.title || standings.descrizione || "Classifica", subtitle, "standings")}
      <div class="table-scroll"><table class="standings" aria-label="Classifica ${escapeHtml(standings.title || "")}">
      <colgroup><col class="col-rank"><col class="col-team"><col class="col-stat" span="10"></colgroup>
      <thead><tr>
        <th scope="col">Pos.</th>
        <th scope="col">Squadre</th>
        <th scope="col" title="Punti">P</th>
        <th scope="col" title="Gare giocate">G</th>
        <th scope="col" title="Gare vinte">GV</th>
        <th scope="col" title="Gare perse">GP</th>
        <th scope="col" title="Set vinti">SV</th>
        <th scope="col" title="Set persi">SP</th>
        <th scope="col" title="Quoziente set">QS</th>
        <th scope="col" title="Punti fatti">PF</th>
        <th scope="col" title="Punti subiti">PS</th>
        <th scope="col" title="Quoziente punti">QP</th>
      </tr></thead><tbody>${rows}</tbody></table></div>
      ${legend}
    </section>` : statusPanel("Classifica non pubblicata.", "empty");
    if (teams.length) {
      let calendarRequest;
      const closeDetails = () => {
        document.querySelector(".standings-detail-row")?.remove();
        document.querySelectorAll(".standings-team-row[aria-expanded='true']").forEach((row) => row.setAttribute("aria-expanded", "false"));
      };
      const toggleTeamResults = async (row) => {
        const wasOpen = row.getAttribute("aria-expanded") === "true";
        closeDetails();
        if (wasOpen) return;
        row.setAttribute("aria-expanded", "true");
        const loading = document.createElement("tr");
        loading.className = "standings-detail-row standings-detail-loading";
        loading.id = row.getAttribute("aria-controls");
        loading.innerHTML = '<td colspan="12"><div class="team-results-loading" role="status">Caricamento gare…</div></td>';
        row.after(loading);
        try {
          calendarRequest ||= api.calendar(standings["commettee-id"] || committee, group, signal);
          const calendarPayload = await calendarRequest;
          if (signal?.aborted || !loading.isConnected) return;
          const matches = Array.isArray(calendarPayload?.data?.matches) ? calendarPayload.data.matches : [];
          loading.outerHTML = teamResultsMarkup(matches, row.dataset.teamId, row.dataset.teamName);
        } catch (error) {
          if (error?.name === "AbortError" || !loading.isConnected) return;
          loading.querySelector(".team-results-loading").textContent = errorMessage(error);
        }
      };
      document.querySelectorAll(".standings-team-row").forEach((row) => {
        row.addEventListener("click", () => toggleTeamResults(row));
        row.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          toggleTeamResults(row);
        });
      });
    }
  } catch (error) {
    if (error.name !== "AbortError") document.querySelector("#standings-content").innerHTML = statusPanel(errorMessage(error), "error");
  }
}
